import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../../constants";
import { useNavigate, useParams } from "react-router-dom";
import {
  FormLayout,
  Form,
  Button,
  Text,
  TextField,
  InlineStack,
  BlockStack,
  Card,
  Badge,
  Link,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks";
import { Customer, CustomerFinance, CustomerForm, FinanceYearDetail } from "types/Customer";
import { useSelector } from "react-redux";
import { RootState } from "configureStore";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import ReplaceFile from "features/replace-file/replace-file";
import SigningHint from "features/signing-hint/signing-hint";
import { Signature } from "types/Signature";
import { validateEmail } from "common/validator";
import {
  sendCreateCustomerQuestionnaire,
  post,
  postQuestionnaireDryRun,
  regenerateQuestionnairePdf,
} from "services/customers.service";
import { createFinanceForYear } from "services/customers.service.typed";
import { isEuCountry } from "utils/eu-countries";
import { performVatCheck } from "services/vat-check.service";
import {
  extractSignersFromList,
  getSignerId,
  isSignatureLockedFile,
  canWithdrawDocument as computeCanWithdrawDocument,
} from "utils/signatureUtils";
import "./customer-finance.scss";
import VatCheckSection from "./vat-check-section";
import UploadFile from "features/upload-file/upload-file";
import PrettyDate from "components/format/PrettyDate";

interface PostData {
  //advance_invoice_deferral_days: number;
  invoice_deferral_days: number;
  //advance_payment_percent: number;
  debt_limit: number;
  year?: number;
  // penalty_percent: number;
  // storage_costs_per_unit: number;
  // non_performance_percent: number;
}

function CustomerFinanceForm({
  customer,
  onSubmit,
  fileEntity,
  financeDetail,
}: {
  customer: Customer;
  onSubmit: () => void;
  fileEntity?: any;
  financeDetail?: FinanceYearDetail | null;
}) {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState<PostData>({
    //advance_invoice_deferral_days: 0,
    invoice_deferral_days: 0,
    //advance_payment_percent: 0,
    debt_limit: 0,
    // penalty_percent: 0,
    // storage_costs_per_unit: 0,
    // non_performance_percent: 0
  });
  const [originalValues, setOriginalValues] = useState<Pick<PostData, "invoice_deferral_days" | "debt_limit">>({
    invoice_deferral_days: 0,
    debt_limit: 0,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingForApprove, setIsLoadingForApprove] = useState(false);
  const [emailValue, setEmailValue] = useState(customer.email || "");
  const [emailError, setEmailError] = useState("");
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [isSendingQuestionnaire, setIsSendingQuestionnaire] = useState(false);
  const [isRegeneratingPdf, setIsRegeneratingPdf] = useState(false);
  // Stepper state for file upload links
  const [clickedLinks, setClickedLinks] = useState<Record<number, boolean>>({});
  // Track pending sanctions types for each upload step
  const [pendingSanctionsTypes, setPendingSanctionsTypes] = useState<
    Record<number, "eu" | "usa" | "lt">
  >({});
  const { companyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Extract sanctions settings - use only year-specific from financeDetail
  const companySettings = {
    isEuSanctionsEnabled:
      financeDetail?.sanctionsSettings?.isEuSanctionsEnabled ?? false,
    isUsaSanctionsEnabled:
      financeDetail?.sanctionsSettings?.isUsaSanctionsEnabled ?? false,
    isLtSanctionsEnabled:
      financeDetail?.sanctionsSettings?.isLtSanctionsEnabled ?? false,
  };

  const { role } = useSelector((state: RootState) => state.authReducer);
  const isUserMaster = role?.permissionsgroups?.includes("Master");
  const isUserAdmin = role?.permissionsgroups?.includes("Admin");
  const isUserManager = customer.isUserManager;
  const isSelfApprover = !!customer.isUserSelfApprover;
  const hasUserSignaturePermission =
    role?.permissionsgroups?.includes("Signature");

  // Use status only from financeRecord
  const isApproved = financeDetail?.financeRecord?.isApproved ?? false;
  const isFinanceFormSent = financeDetail?.financeRecord?.isFinanceFormSent ?? false;
  const isFinanceActive = financeDetail?.financeRecord?.isFinanceActive ?? false;

  const isDocumentSigned = Boolean(fileEntity && fileEntity.isSigned);
  // Owner's signing rule: a document generated from a template may never have
  // a signature/signature request removed one by one — only withdrawn whole.
  // The finance approval document is always template-built, but check the
  // authoritative signaturesLocked flag rather than assume it, same as the
  // file view containers (withdraw itself is separately gated below — the
  // backend never marks a finance document withdrawSupported).
  const isFileSignatureLocked = isSignatureLockedFile(fileEntity);
  const isFileWithdrawn = Boolean(fileEntity?.withdrawnAt);
  const canWithdrawFinanceDocument =
    Boolean(fileEntity?.id) &&
    computeCanWithdrawDocument(fileEntity, {
      currentUserId: role?.user,
      isMaster: isUserMaster,
    });
  const isFormDirty =
    Number(formValues.invoice_deferral_days) !== originalValues.invoice_deferral_days ||
    Number(formValues.debt_limit) !== originalValues.debt_limit;
  const showActionButtons = !isDocumentSigned && (!isApproved || isFormDirty);

  // Check questionnaire status - use ONLY year-specific forms
  const formsToCheck = financeDetail?.forms ?? [];
  const isQuestionnaireCompleted = formsToCheck.some((form: CustomerForm) => !!form.formSignature);
  const hasCompletedQuestionnaireInPast = financeDetail?.hasCompletedQuestionnaireInPast ?? false;
  const skipQuestionnaireRequirementIfCompletedInPast = financeDetail?.skipQuestionnaireRequirementIfCompletedInPast ?? true;
  const effectiveQuestionnaireCompleted =
    isQuestionnaireCompleted || (skipQuestionnaireRequirementIfCompletedInPast && hasCompletedQuestionnaireInPast);

  const latestForm = formsToCheck[0];
  const signedForm = formsToCheck.find((form: CustomerForm) => !!form.formSignature) ?? null;
  const formKeyCreatedAt = latestForm?.formKeyCreatedAt ?? null;
  const formSignature = signedForm?.formSignature ?? null;
  const hasEmail = !!customer.email;

  // Get sanctions documents - use ONLY year-specific
  const sanctionsDocuments = financeDetail?.sanctionsDocuments ?? { eu: [], usa: [], lt: [] };
  const euSanctionsFile = sanctionsDocuments?.eu?.[0];
  const usaSanctionsFile = sanctionsDocuments?.usa?.[0];
  const ltSanctionsFile = sanctionsDocuments?.lt?.[0];

  // Get VAT check entries and settings - use ONLY year-specific
  const vatCheckEntries = financeDetail?.vatChecks || [];
  const latestVatCheckEntry = vatCheckEntries.length ? vatCheckEntries[vatCheckEntries.length - 1] :null;
  const vatCheckDocument = latestVatCheckEntry?.file;
  // Use year-specific VAT check setting
  const isVatCheckEnabled =
    financeDetail?.vatCheckSettings?.isVatCheckEnabled ?? false;
  const customerCountry = customer?.country || customer?.meta?.valstybe;
  // Extract country code from VAT code (first 2 letters) for EU check
  // Use VAT code country if available, otherwise fall back to address country
  const vatCodeCountry =
    customer?.vatCode && customer.vatCode.length >= 2
      ? customer.vatCode.substring(0, 2).toUpperCase()
      : null;
  const countryForEuCheck = vatCodeCountry || customerCountry;
  const isCustomerEuCountry = isEuCountry(countryForEuCheck);
  // The questionnaire answer is authoritative: a partner who declared he is not
  // a VAT payer has no VAT number to validate, so VIES can only ever answer
  // "invalid" and the whole approval chain would dead-end there.
  const isKnownNonVatPayer = customer?.isVatPayer === false;
  const shouldShowVatCheck =
    isVatCheckEnabled && isCustomerEuCountry && !isKnownNonVatPayer;

  // VAT check state
  const [isPerformingVatCheck, setIsPerformingVatCheck] = useState(false);

  useEffect(() => {
    const record = financeDetail?.financeRecord;
    if (record) {
      const { id, createdAt, updatedAt, dateTo, ...filteredFinance } = record as any;
      setFormValues((prev) => ({
        ...prev,
        ...filteredFinance,
        year: financeDetail?.year || prev.year,
      }));
      setOriginalValues({
        invoice_deferral_days: Number(record.invoice_deferral_days) || 0,
        debt_limit: Number(record.debt_limit) || 0,
      });
    } else if (financeDetail?.year) {
      setFormValues((prev) => ({
        ...prev,
        year: financeDetail.year,
      }));
      setOriginalValues({ invoice_deferral_days: 0, debt_limit: 0 });
    }
  }, [financeDetail?.financeRecord, financeDetail?.year]);

  useEffect(() => {
    setEmailValue(customer.email || "");
  }, [customer.email]);

  const validateField = (field: string, value: any): string => {
    switch (field) {
      case "advance_invoice_deferral_days":
      case "invoice_deferral_days":
        return value < 0 ? t("Must be a positive integer") : "";
      case "advance_payment_percent":
      case "penalty_percent":
      case "non_performance_percent":
        return value < 0 || value > 100
          ? t("Must be a percentage between 0 and 100")
          : "";
      case "debt_limit":
      case "storage_costs_per_unit":
        return value < 0 ? t("Must be a positive number") : "";
      case "dateTo":
        return !value ? t("Date is required") : "";
      default:
        return "";
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const isFormValid = useCallback(() => {
    const currentErrors: Record<string, string> = {};
    Object.entries(formValues).forEach(([field, value]) => {
      const error = validateField(field, value);
      if (error) currentErrors[field] = error;
    });

    if (JSON.stringify(currentErrors) !== JSON.stringify(errors)) {
      setErrors(currentErrors);
    }

    return Object.keys(currentErrors).length === 0;
  }, [formValues, errors]);

  const handleFormSubmit = async (isApprove?: boolean) => {
    if (!isFormValid()) return;
    isApprove ? setIsLoadingForApprove(true) : setIsLoading(true);
    try {
      if (companyId && financeDetail?.year) {
        const financeId = financeDetail?.financeRecord?.id ?? undefined;
        await createFinanceForYear(
          companyId,
          customer.id,
          financeDetail.year,
          formValues,
          financeId
        );
      }
      onSubmit();
    } catch (e) {
      setIsLoading(false);
      setIsLoadingForApprove(false);
    } finally {
      setIsLoading(false);
      setIsLoadingForApprove(false);
    }
  };

  const handleSaveClick = () => {
    if (isFormValid()) {
      dispatch(setModalxComponent("CustomerFinanceSaveConfirm"));
      dispatch(
        setModalxComponentProps({
          companyId,
          customerId: customer.id,
          formValues,
          year: financeDetail?.year,
          financeId: financeDetail?.financeRecord?.id,
        })
      );
    }
  };

  // Company director with approval right: saving the terms IS the approval
  // (backend approves in the same request), so instead of the manager-approval
  // flow this opens the same save-confirm modal with selfApprove: true.
  const handleSelfApproveClick = () => {
    if (isFormValid()) {
      dispatch(setModalxComponent("CustomerFinanceSaveConfirm"));
      dispatch(
        setModalxComponentProps({
          companyId,
          customerId: customer.id,
          formValues,
          year: financeDetail?.year,
          financeId: financeDetail?.financeRecord?.id,
          selfApprove: true,
          shouldRegenerateDocument: !!(
            isFinanceFormSent &&
            fileEntity?.id &&
            !fileEntity?.isSigned
          ),
        })
      );
    }
  };

  const customerApprovalHandler = async () => {
    await handleFormSubmit(true);
    dispatch(setModalxComponent("CustomerApproveConfirmPopup"));
    dispatch(
      setModalxComponentProps({
        companyId,
        customerId: customer.id,
        year: financeDetail?.year,
        shouldRegenerateDocument: !!(
          isFinanceFormSent &&
          fileEntity?.id &&
          !fileEntity?.isSigned
        ),
        financeId: financeDetail?.financeRecord?.id,
      })
    );
  };

  const financeSignHandler = async () => {
    dispatch(setModalxComponent("CustomerFinaceSignConfirmPopup"));
    dispatch(
      setModalxComponentProps({
        companyId,
        customerId: customer.id,
        year: financeDetail?.year,
        financeId: financeDetail?.financeRecord?.id,
      })
    );
  };

  const handleOnFileChange = () => {};

  const signingStepsRequired =
    fileEntity && fileEntity?.signingStepsRequired
      ? fileEntity?.signingStepsRequired
      : false;

  const extracted = extractSignersFromList(fileEntity?.signersList);
  const contactSigners = extracted.contactSigners
    .map((signer: any) => getSignerId(signer))
    .filter((id: string | null): id is string => !!id);
  const permissionUserSigner = getSignerId(extracted.permissionUserSigner) ?? "";
  const userSigner = getSignerId(extracted.userSigner) ?? "";

  const handleCancelSignerRequest = async (
    fileId: string,
    contactSigner: string,
    companyUserSigner: string,
    customerId: string,
    nontechuser: string,
    signType: string
  ) => {
    dispatch(setModalxComponent("SignRequestCancelPopup"));
    dispatch(
      setModalxComponentProps({
        fileId,
        contactSigner,
        companyUserSigner,
        customerId,
        nontechuser,
        signType,
      })
    );
  };

  const handleWithdrawClick = () => {
    if (!fileEntity?.id) return;
    dispatch(setModalxComponent("WithdrawDocumentConfirm"));
    dispatch(
      setModalxComponentProps({
        fileId: fileEntity.id,
        onDone: () => onSubmit(),
      })
    );
  };

  const handleSendEmailWithAttachment = (
    fileId: string,
    customerId: string
  ) => {
    dispatch(setModalxComponent("ManualSignSendMailConfirm"));
    dispatch(setModalxComponentProps({ customerId, fileId, companyId }));
  };

  const FileSignUserAssignHandler = (
    fileId: string,
    relatedTo: string,
    permissionUserSigner: string,
    userSigner: string
  ) => {
    dispatch(setModalxComponent("FileSignUserAssignPopup"));
    dispatch(
      setModalxComponentProps({
        fileId,
        companyId,
        customerId: customer.id,
        relatedTo,
        permissionUserSigner,
        userSigner,
        subject: "finance",
        year: financeDetail?.year,
        financeId: financeDetail?.financeRecord?.id,
      })
    );
  };

  const handleSignRequest = (fileId: string, relatedTo?: string) => {
    dispatch(setModalxComponent("FileSignAssignPopup"));
    dispatch(
      setModalxComponentProps({
        fileId,
        companyId,
        customerId: customer.id,
        relatedTo,
        year: financeDetail?.year,
        financeId: financeDetail?.financeRecord?.id,
      })
    );
  };

  const handleUploadClick = async (
    id: string,
    contactSigners: string[],
    companyUserSigner: string,
    customerId: string,
    nontechuser: string,
    signType: string
  ) => {
    dispatch(setModalxComponent("SignRequestConfirmPopup"));
    dispatch(
      setModalxComponentProps({
        fileId: id,
        contactSigners,
        companyUserSigner,
        customerId,
        nontechuser,
        signType,
        subject: "finance",
      })
    );
  };

  const handleEmailChange = (value: string) => {
    setEmailValue(value);
    if (emailError) {
      setEmailError("");
    }
  };

  const handleSaveEmail = async () => {
    if (!emailValue.trim()) {
      setEmailError(t("field_is_required"));
      return;
    }
    if (!validateEmail(emailValue)) {
      setEmailError(t("invalid_email_address"));
      return;
    }

    setIsSavingEmail(true);
    setEmailError("");
    try {
      if (companyId) {
        // Use POST endpoint: POST /{companyId}/customers
        // Include customerId to indicate this is an update, not a create
        await post(companyId, {
          customerId: customer.id,
          email: emailValue,
          name: customer.name,
          phone: customer.phone || "",
          contactPerson: customer.contactPerson || "",
          vatCode: customer.vatCode || "",
          companyCode: customer.companyCode,
          address: customer.address || "",
          companyId,
          assignedUser: customer.assignedUser?.id || "",
        });
        // Clear error on success
        setEmailError("");
        onSubmit();
      }
    } catch (e: any) {
      // Extract error message from API response
      const errorMessage =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        t("error_saving_email");
      setEmailError(errorMessage);
    } finally {
      setIsSavingEmail(false);
    }
  };

  const handleSendQuestionnaire = async () => {
    if (!companyId || !financeDetail?.year) return;
    setIsSendingQuestionnaire(true);
    try {
      if (customer.vatCode?.trim()) {
        try {
          const preview = await postQuestionnaireDryRun(
            companyId,
            customer.id,
            financeDetail.year
          );
          if (preview?.crossCompany) {
            dispatch(
              setModalxOptions({
                title: t("questionnaire_cross_company_confirm_title")
              })
            );
            dispatch(setModalxComponent("CustomerQuestionnaireCrossCompanyConfirm"));
            dispatch(
              setModalxComponentProps({
                companyId,
                customerId: customer.id,
                year: financeDetail.year,
                sourceCompanyName: preview.sourceCompanyName || "",
                attachmentCount: preview.attachmentCount || 0,
                onSuccess: () => onSubmit(),
              })
            );
            return;
          }
        } catch (dryRunError) {
          console.error("Error running questionnaire dry-run", dryRunError);
        }
      }
      await sendCreateCustomerQuestionnaire(companyId, customer.id, financeDetail.year);
      onSubmit();
    } catch (e) {
      console.error("Error sending questionnaire", e);
    } finally {
      setIsSendingQuestionnaire(false);
    }
  };

  const handleRegenerateQuestionnairePdf = async () => {
    if (!companyId || !signedForm?.id) return;
    setIsRegeneratingPdf(true);
    try {
      await regenerateQuestionnairePdf(companyId, customer.id, signedForm.id);
      onSubmit();
    } catch (e) {
      console.error("Error regenerating questionnaire PDF", e);
    } finally {
      setIsRegeneratingPdf(false);
    }
  };

  // Helper function to check if all required sanctions steps are completed
  const areRequiredSanctionsCompleted = (): boolean => {
    const required: boolean[] = [];
    if (companySettings.isEuSanctionsEnabled) {
      required.push(!!euSanctionsFile);
    }
    if (companySettings.isUsaSanctionsEnabled) {
      required.push(!!usaSanctionsFile);
    }
    if (companySettings.isLtSanctionsEnabled) {
      required.push(!!ltSanctionsFile);
    }
    return required.length === 0 || required.every((val) => val === true);
  };

  // Helper function to check if VAT check is completed
  const isVatCheckCompleted = (): boolean => {
    if (!shouldShowVatCheck) return true; // Not required
    // VAT check is completed if latest entry has both file and isValid is true (valid)
    return !!(
      latestVatCheckEntry?.file && latestVatCheckEntry?.isValid === true
    );
  };

  // Handler for automatic VAT check
  const handlePerformVatCheck = async () => {
    if (!companyId || !customer.id || !financeDetail?.year) {
      return;
    }

    setIsPerformingVatCheck(true);
    try {
      // Perform VAT check via backend API (backend handles EU VIES API call)
      await performVatCheck(
        companyId,
        customer.id,
        financeDetail.year,
        financeDetail?.financeRecord?.id
      );

      // Refresh data
      onSubmit();
    } catch (error) {
      console.error("Error performing VAT check:", error);
    } finally {
      setIsPerformingVatCheck(false);
    }
  };

  // Helper function to check if a step is green (completed)
  const isStepGreen = (stepNumber: number): boolean => {
    switch (stepNumber) {
      case 1: // Questionnaire - use effective so we can skip when setting + any-year completed
        return effectiveQuestionnaireCompleted;
      case 2: // EU Sanctions
        return !companySettings.isEuSanctionsEnabled || !!euSanctionsFile;
      case 3: // USA Sanctions
        return !companySettings.isUsaSanctionsEnabled || !!usaSanctionsFile;
      case 4: // LT Sanctions
        return !companySettings.isLtSanctionsEnabled || !!ltSanctionsFile;
      case 5: // VAT Check
        return !shouldShowVatCheck || isVatCheckCompleted();
      case 6: // Manager Approval
        return !!isApproved;
      case 7: // Generate Document
        return !!isFinanceFormSent;
      case 8: // Collect Signatures
        return !!isFinanceActive;
      case 9: // Create Order
        return !!isFinanceActive;
      default:
        return false;
    }
  };

  // Helper function to check if the step just above is green
  const isPreviousStepGreen = (stepNumber: number): boolean => {
    if (stepNumber <= 1) return false;
    return isStepGreen(stepNumber - 1);
  };

  // Helper function to check if there are green steps above and below a given step
  const isStepBetweenGreenSteps = (stepNumber: number): boolean => {
    // Check if there's a green step above (lower step number)
    let hasGreenAbove = false;
    for (let i = 1; i < stepNumber; i++) {
      if (isStepGreen(i)) {
        hasGreenAbove = true;
        break;
      }
    }

    // Check if there's a green step below (higher step number)
    let hasGreenBelow = false;
    for (let i = stepNumber + 1; i <= 9; i++) {
      if (isStepGreen(i)) {
        hasGreenBelow = true;
        break;
      }
    }

    return hasGreenAbove && hasGreenBelow;
  };

  // Helper function to get step status (green = completed, yellow = in progress, red = blocking/pending, grey = not started)
  const getStepStatus = (
    stepNumber: number
  ): "green" | "yellow" | "red" | "grey" => {
    switch (stepNumber) {
      case 1: // Questionnaire - use effective so step shows complete when setting + any-year questionnaire
        return effectiveQuestionnaireCompleted
          ? "green"
          : hasEmail
          ? "yellow"
          : "grey";
      case 2: // First file upload (EU)
        if (!companySettings.isEuSanctionsEnabled) {
          return "green"; // Show as completed if disabled
        }
        if (euSanctionsFile) {
          return "green"; // Completed
        }
        if (clickedLinks[1]) {
          return "yellow"; // In progress
        }
        // If previous step is green → YELLOW (user can perform actions) — check before RED
        if (isPreviousStepGreen(2) || effectiveQuestionnaireCompleted) {
          return "yellow";
        }
        // RED when any pending step between two greens (at any level)
        if (isStepBetweenGreenSteps(2)) {
          return "red";
        }
        return "grey";
      case 3: // Second file upload (USA)
        if (!companySettings.isUsaSanctionsEnabled) {
          return "green"; // Show as completed if disabled
        }
        if (usaSanctionsFile) {
          return "green"; // Completed
        }
        if (clickedLinks[2]) {
          return "yellow"; // In progress
        }
        const euRequirementFor3 = companySettings.isEuSanctionsEnabled
          ? !!euSanctionsFile
          : true;
        // If previous steps are done → YELLOW (user can perform actions) — check before RED
        if (isPreviousStepGreen(3) || (effectiveQuestionnaireCompleted && euRequirementFor3)) {
          return "yellow";
        }
        // RED when any pending step between two greens (at any level)
        if (isStepBetweenGreenSteps(3)) {
          return "red";
        }
        return "grey";
      case 4: // Third file upload (LT)
        if (!companySettings.isLtSanctionsEnabled) {
          return "green"; // Show as completed if disabled
        }
        if (ltSanctionsFile) {
          return "green"; // Completed
        }
        if (clickedLinks[3]) {
          return "yellow"; // In progress
        }
        // Cascade through previous steps: USA -> EU -> Questionnaire
        const previousStepForLT = companySettings.isUsaSanctionsEnabled
          ? !!usaSanctionsFile
          : companySettings.isEuSanctionsEnabled
          ? !!euSanctionsFile
          : effectiveQuestionnaireCompleted;
        // If previous steps are done → YELLOW (user can perform actions) — check before RED
        if (isPreviousStepGreen(4) || previousStepForLT) {
          return "yellow";
        }
        // RED when any pending step between two greens (at any level)
        if (isStepBetweenGreenSteps(4)) {
          return "red";
        }
        return "grey";
      case 5: // VAT Check
        if (!shouldShowVatCheck) {
          return "green"; // Show as completed if not required
        }
        if (isVatCheckCompleted()) {
          return "green"; // Completed
        }
        if (isPerformingVatCheck) {
          return "yellow"; // In progress
        }
        // If previous step is green → YELLOW (user can perform actions) — check before RED
        if (isPreviousStepGreen(5)) {
          return "yellow";
        }
        // RED when any pending step between two greens (at any level)
        if (isStepBetweenGreenSteps(5)) {
          return "red";
        }
        return "grey";
      case 6: // Manager approval
        if (isApproved) {
          return "green";
        }
        // YELLOW only when ALL prerequisites are done (questionnaire, sanctions, VAT if required) — check before RED
        const allPrerequisitesForApproval =
          effectiveQuestionnaireCompleted &&
          areRequiredSanctionsCompleted() &&
          (!shouldShowVatCheck || isVatCheckCompleted());
        if (allPrerequisitesForApproval) {
          return "yellow";
        }
        // RED when any pending step between two greens (at any level)
        if (isStepBetweenGreenSteps(6)) {
          return "red";
        }
        return "grey";
      case 7: // Generate document
        return isFinanceFormSent ? "green" : isApproved ? "yellow" : "grey";
      case 8: // Collect signatures
        return isFinanceActive
          ? "green"
          : isFinanceFormSent
          ? "yellow"
          : "grey";
      case 9: // Create order
        return isFinanceActive ? "green" : "grey";
      default:
        return "grey";
    }
  };

  const handleLinkClick = (linkNumber: number) => {
    setClickedLinks((prev) => ({ ...prev, [linkNumber]: true }));

    // Map step numbers to sanctions types
    const sanctionsTypeMap: Record<number, "eu" | "usa" | "lt"> = {
      1: "eu",
      2: "usa",
      3: "lt",
    };

    // Store the pending sanctions type for this step
    if (sanctionsTypeMap[linkNumber]) {
      setPendingSanctionsTypes((prev) => ({
        ...prev,
        [linkNumber]: sanctionsTypeMap[linkNumber],
      }));
    }
  };

  const handleCreateOrder = () => {
    navigate(`/app/${companyId}/customers/${customer.id}/orders/new`);
  };

  // Render stepper step component
  const renderStep = (
    stepNumber: number,
    title: string,
    content: React.ReactNode,
    showConnector: boolean = true
  ) => {
    const status = getStepStatus(stepNumber);
    const borderClass = `border-${status}`;

    return (
      <>
        <div className="card-wrapper">
          <div className={`card-box ${borderClass}`}>
            <Card roundedAbove="sm">
              <Text as="h2" variant="headingSm">
                {title}
              </Text>
              {content}
            </Card>
          </div>
          {showConnector && (
            <div className="connector-wrapper">
              <div className="vertical-line"></div>
              <div className="arrow-down"></div>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <Card roundedAbove="sm">
      <BlockStack gap="200">
        <InlineStack align="space-between">
          <Text as="h2" variant="headingSm">
            {t("master_agreement")}&nbsp;
            {financeDetail?.financeRecord?.isApproved && (
              <Badge tone="success">{t("master_agreement_approved")}</Badge>
            )}
            {financeDetail?.financeRecord?.isApproved === false && (
              <Badge tone="attention">
                {t("master_agreement_not_approved")}
              </Badge>
            )}
          </Text>
          {isFinanceFormSent && !isFinanceActive && (
            <Badge progress="incomplete" tone="attention">
              {t("signature_pending")}
            </Badge>
          )}
          {isFinanceActive && (
            <Badge tone="success">{t("finance_activated")}</Badge>
          )}
        </InlineStack>
        <Text as="p" tone="subdued">
          {t("master_agreement_description")}
        </Text>

        <div style={{ marginTop: "20px" }}>
          <BlockStack gap="500">
            {/* Step 1: Customer Questionnaire - Always visible */}
            {renderStep(
              1,
              t("customer_questionnaire"),
              <BlockStack gap="200">
                {!hasEmail ? (
                  <>
                    <Text as="p" tone="subdued">
                      {t("email_required_for_questionnaire")}
                    </Text>
                    <FormLayout>
                      <TextField
                        label={t("email")}
                        value={emailValue}
                        onChange={handleEmailChange}
                        error={emailError}
                        type="email"
                        autoComplete="email"
                        requiredIndicator
                      />
                      <Button
                        onClick={handleSaveEmail}
                        loading={isSavingEmail}
                        disabled={!emailValue.trim() || !!emailError}
                        variant="primary"
                      >
                        {t("save_email")}
                      </Button>
                    </FormLayout>
                  </>
                ) : effectiveQuestionnaireCompleted ? (
                  <>
                    {isQuestionnaireCompleted ? (
                      <>
                        <Text as="p" tone="success">
                          {t("customer_questionnaire-signed")}
                        </Text>
                        {formSignature && signedForm && (
                          <BlockStack gap="100">
                            {signedForm.formKeyCreatedAt && (
                              <Text as="p" tone="subdued">
                                {t("questionnaire_sent")}:{" "}
                                <PrettyDate showTime dateString={signedForm.formKeyCreatedAt} />
                              </Text>
                            )}
                            <Text as="p" tone="subdued">
                              {t("questionnaire_signed_at")}:{" "}
                              <PrettyDate showTime dateString={formSignature.datetime} />
                            </Text>
                            <Text as="p" tone="subdued">
                              {(() => {
                                let signerName = "";
                                if (formSignature.signerFirstName || formSignature.signerLastName) {
                                  signerName = `${formSignature.signerFirstName || ""} ${formSignature.signerLastName || ""}`.trim();
                                } else if (formSignature.signerFullName) {
                                  signerName = formSignature.signerFullName;
                                }
                                const nameAndPosition = [signerName, formSignature.signerPosition]
                                  .filter(Boolean)
                                  .join(", ");
                                return nameAndPosition || "-";
                              })()}
                            </Text>
                            <Text as="p" tone="subdued">
                              IP: {formSignature.ip}
                            </Text>
                          </BlockStack>
                        )}
                      </>
                    ) : (
                      <Text as="p" tone="success">
                        {t("questionnaire_satisfied_from_previous_year")}
                      </Text>
                    )}
                    {isQuestionnaireCompleted && signedForm?.id && (
                      <>
                        {signedForm.questionnaireFileId?.id ? (
                          <Link
                            url={`${API_URL}/${companyId}/files/download/${signedForm.questionnaireFileId.id}`}
                            target="_blank"
                          >
                            {signedForm.questionnaireFileId.title ||
                              signedForm.questionnaireFileId.name ||
                              t("download_file")}
                          </Link>
                        ) : (
                          <Button
                            onClick={handleRegenerateQuestionnairePdf}
                            loading={isRegeneratingPdf}
                          >
                            {t("regenerate_questionnaire_pdf")}
                          </Button>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {formKeyCreatedAt && !formSignature ? (
                      <>
                        <Text as="p" tone="subdued">
                          {t("questionnaire_sent_waiting_signature")}
                        </Text>
                        <Text as="p" tone="subdued">
                          {t("questionnaire_sent")}:{" "}
                          <PrettyDate showTime dateString={formKeyCreatedAt} />
                        </Text>
                      </>
                    ) : (
                      <>
                        <Text as="p" tone="subdued">
                          {t("send_questionnaire_to_customer")}
                        </Text>
                        <Button
                          onClick={handleSendQuestionnaire}
                          loading={isSendingQuestionnaire}
                          variant="primary"
                        >
                          {t("send_questionnaire")}
                        </Button>
                      </>
                    )}
                  </>
                )}
              </BlockStack>
            )}

            {/* Step 2: First File Upload - EU Sanctions Check - Conditionally visible */}
            {/* Show if enabled in settings OR if file is uploaded */}
            {(companySettings.isEuSanctionsEnabled || !!euSanctionsFile) &&
              renderStep(
                2,
                t("eu_sanctions_check"),
                <BlockStack gap="200">
                  {effectiveQuestionnaireCompleted ? (
                    <>
                      {euSanctionsFile ? (
                        <BlockStack gap="200">
                          <Link
                            url={`${API_URL}/${companyId}/files/download/${euSanctionsFile.id}`}
                            target="_blank"
                          >
                            {euSanctionsFile.name ||
                              euSanctionsFile.title ||
                              t("download_file")}
                          </Link>
                        </BlockStack>
                      ) : !clickedLinks[1] ? (
                        <BlockStack gap="200">
                          <Text as="p" tone="subdued">
                            {t("eu_sanctions_check_description")}
                          </Text>
                          <Link
                            url="https://sanctionsmap.eu/"
                            target="_blank"
                            onClick={() => handleLinkClick(1)}
                          >
                            {t("eu_sanctions_check")}
                          </Link>
                        </BlockStack>
                      ) : (
                        <BlockStack gap="200">
                          <Text as="p" tone="subdued">
                            {t("upload_screenshot_description")}
                          </Text>
                          <UploadFile
                            onChange={() => {}}
                            subject="customer"
                            subjectId={customer.id}
                            categoryType="internal"
                            sanctionsType={
                              pendingSanctionsTypes[1] || undefined
                            }
                            fileCategory={
                              financeDetail?.sanctionsSettings?.sanctionsFileCategoryEu
                            }
                            fileType={
                              financeDetail?.sanctionsSettings?.sanctionsFileTypeEu
                            }
                            year={financeDetail?.year}
                            customerFinance={financeDetail?.financeRecord?.id}
                          />
                        </BlockStack>
                      )}
                    </>
                  ) : (
                    <Text as="p" tone="subdued">
                      {t("complete_previous_step")}
                    </Text>
                  )}
                </BlockStack>
              )}

            {/* Step 3: Second File Upload - USA Sanctions Check - Conditionally visible */}
            {/* Show if enabled in settings OR if file is uploaded */}
            {(companySettings.isUsaSanctionsEnabled || !!usaSanctionsFile) &&
              renderStep(
                3,
                t("usa_sanctions_check"),
                <BlockStack gap="200">
                  {(() => {
                    // If file already exists, always show it (allow viewing/downloading)
                    if (usaSanctionsFile) {
                      return (
                        <BlockStack gap="200">
                          <Link
                            url={`${API_URL}/${companyId}/files/download/${usaSanctionsFile.id}`}
                            target="_blank"
                          >
                            {usaSanctionsFile.name ||
                              usaSanctionsFile.title ||
                              t("download_file")}
                          </Link>
                        </BlockStack>
                      );
                    }

                    // Check if previous required step is completed (only for new uploads)
                    // Match the step status logic: requires questionnaire AND EU file (if EU enabled)
                    const euRequirement = companySettings.isEuSanctionsEnabled
                      ? !!euSanctionsFile
                      : true;
                    const previousStepCompleted =
                      effectiveQuestionnaireCompleted && euRequirement;

                    if (!previousStepCompleted) {
                      return (
                        <Text as="p" tone="subdued">
                          {t("complete_previous_step")}
                        </Text>
                      );
                    }

                    // Show upload interface
                    return !clickedLinks[2] ? (
                      <BlockStack gap="200">
                        <Text as="p" tone="subdued">
                          {t("usa_sanctions_check_description")}
                        </Text>
                        <Link
                          url="https://sanctionssearch.ofac.treas.gov/"
                          target="_blank"
                          onClick={() => handleLinkClick(2)}
                        >
                          {t("usa_sanctions_check")}
                        </Link>
                      </BlockStack>
                    ) : (
                      <BlockStack gap="200">
                        <Text as="p" tone="subdued">
                          {t("upload_screenshot_description")}
                        </Text>
                        <UploadFile
                          onChange={() => {}}
                          subject="customer"
                          subjectId={customer.id}
                          categoryType="internal"
                          sanctionsType={pendingSanctionsTypes[2] || undefined}
                          fileCategory={
                            financeDetail?.sanctionsSettings?.sanctionsFileCategoryUsa
                          }
                          fileType={
                            financeDetail?.sanctionsSettings?.sanctionsFileTypeUsa
                          }
                          year={financeDetail?.year}
                          customerFinance={financeDetail?.financeRecord?.id}
                        />
                      </BlockStack>
                    );
                  })()}
                </BlockStack>
              )}

            {/* Step 4: Third File Upload - LT Sanctions Check - Conditionally visible */}
            {/* Show if enabled in settings OR if file is uploaded */}
            {(companySettings.isLtSanctionsEnabled || !!ltSanctionsFile) &&
              renderStep(
                4,
                t("lt_sanctions_check"),
                <BlockStack gap="200">
                  {(() => {
                    // If file already exists, always show it (allow viewing/downloading)
                    if (ltSanctionsFile) {
                      return (
                        <BlockStack gap="200">
                          <Link
                            url={`${API_URL}/${companyId}/files/download/${ltSanctionsFile.id}`}
                            target="_blank"
                          >
                            {ltSanctionsFile.name ||
                              ltSanctionsFile.title ||
                              t("download_file")}
                          </Link>
                        </BlockStack>
                      );
                    }

                    // Check if previous required step is completed (only for new uploads)
                    const previousStepCompleted =
                      companySettings.isUsaSanctionsEnabled
                        ? !!usaSanctionsFile
                        : companySettings.isEuSanctionsEnabled
                        ? !!euSanctionsFile
                        : effectiveQuestionnaireCompleted;

                    if (!previousStepCompleted) {
                      return (
                        <Text as="p" tone="subdued">
                          {t("complete_previous_step")}
                        </Text>
                      );
                    }

                    // Show upload interface
                    return !clickedLinks[3] ? (
                      <BlockStack gap="200">
                        <Text as="p" tone="subdued">
                          {t("lt_sanctions_check_description")}
                        </Text>
                        <Link
                          url="https://fntt.lrv.lt/lt/tarptautines-finansines-sankcijos/sankcionuotu-asmenu-sarasas/"
                          target="_blank"
                          onClick={() => handleLinkClick(3)}
                        >
                          {t("lt_sanctions_check")}
                        </Link>
                      </BlockStack>
                    ) : (
                      <BlockStack gap="200">
                        <Text as="p" tone="subdued">
                          {t("upload_screenshot_description")}
                        </Text>
                        <UploadFile
                          onChange={() => {}}
                          subject="customer"
                          subjectId={customer.id}
                          categoryType="internal"
                          sanctionsType={pendingSanctionsTypes[3] || undefined}
                          fileCategory={
                            financeDetail?.sanctionsSettings?.sanctionsFileCategoryLt
                          }
                          fileType={
                            financeDetail?.sanctionsSettings?.sanctionsFileTypeLt
                          }
                          year={financeDetail?.year}
                          customerFinance={financeDetail?.financeRecord?.id}
                        />
                      </BlockStack>
                    );
                  })()}
                </BlockStack>
              )}

            {/* Step 5: VAT Check - Conditionally visible (EU countries only) */}
            {shouldShowVatCheck &&
              renderStep(
                5,
                t("vat_check"),
                <BlockStack gap="200">
                  {(() => {
                    // Check if previous required steps are completed
                    const previousStepCompleted =
                      effectiveQuestionnaireCompleted &&
                      areRequiredSanctionsCompleted();

                    if (!previousStepCompleted) {
                      return (
                        <Text as="p" tone="subdued">
                          {t("complete_previous_step")}
                        </Text>
                      );
                    }

                    // VAT check interface (always available once prerequisites met)
                    return (
                      <BlockStack gap="200">
                        <VatCheckSection
                          customer={customer}
                          companyId={companyId}
                          vatCheckEntries={vatCheckEntries}
                          vatCheckDocument={vatCheckDocument}
                          isQuestionnaireCompleted={effectiveQuestionnaireCompleted}
                          areRequiredSanctionsCompleted={
                            areRequiredSanctionsCompleted
                          }
                          onPerformVatCheck={handlePerformVatCheck}
                          isPerformingVatCheck={isPerformingVatCheck}
                          canPerformVatCheck={
                            !!(
                              customer.vatCode && isEuCountry(countryForEuCheck)
                            )
                          }
                          year={financeDetail?.year}
                          vatCheckFileCategory={
                            financeDetail?.vatCheckSettings?.vatCheckFileCategory
                          }
                          vatCheckFileType={
                            financeDetail?.vatCheckSettings?.vatCheckFileType
                          }
                          customerFinance={financeDetail?.financeRecord?.id}
                        />
                      </BlockStack>
                    );
                  })()}
                </BlockStack>
              )}

            {/* Step 6: Manager Approval - Always visible */}
            {renderStep(
              6,
              t("manager_approval"),
              <BlockStack gap="200">
                {effectiveQuestionnaireCompleted ? (
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                    }}
                  >
                    <FormLayout>
                      <TextField
                        label={t("invoice_deferral_days")}
                        value={String(formValues.invoice_deferral_days ?? "")}
                        type="number"
                        readOnly={isDocumentSigned}
                        onChange={(value) =>
                          handleInputChange("invoice_deferral_days", parseFloat(value) || 0)
                        }
                        error={errors.invoice_deferral_days}
                        requiredIndicator
                        autoComplete="off"
                      />
                      <TextField
                        label={t("debt_limit")}
                        value={String(formValues.debt_limit ?? "")}
                        type="number"
                        readOnly={isDocumentSigned}
                        onChange={(value) =>
                          handleInputChange("debt_limit", parseFloat(value) || 0)
                        }
                        error={errors.debt_limit}
                        requiredIndicator
                        autoComplete="off"
                      />
                      {showActionButtons && (
                        isSelfApprover ? (
                          <InlineStack align="center">
                            <Button
                              loading={isLoadingForApprove}
                              disabled={
                                !isFormValid() ||
                                !areRequiredSanctionsCompleted() ||
                                (shouldShowVatCheck && !isVatCheckCompleted())
                              }
                              onClick={handleSelfApproveClick}
                              variant="primary"
                            >
                              {t("save_and_approve")}
                            </Button>
                          </InlineStack>
                        ) : (
                          <InlineStack align="space-between">
                            <Button
                              loading={isLoading}
                              disabled={
                                !isFormValid() ||
                                !areRequiredSanctionsCompleted() ||
                                (shouldShowVatCheck && !isVatCheckCompleted())
                              }
                              onClick={handleSaveClick}
                            >
                              {t("save")}
                            </Button>
                            {(isUserMaster || hasUserSignaturePermission) && (
                              <Button
                                loading={isLoadingForApprove}
                                disabled={
                                  !isFormValid() ||
                                  !areRequiredSanctionsCompleted() ||
                                  (shouldShowVatCheck && !isVatCheckCompleted())
                                }
                                onClick={customerApprovalHandler}
                              >
                                {t("save_and_approve")}
                              </Button>
                            )}
                          </InlineStack>
                        )
                      )}
                    </FormLayout>
                  </Form>
                ) : (
                  <Text as="p" tone="subdued">
                    {t("complete_previous_step")}
                  </Text>
                )}
              </BlockStack>
            )}

            {/* Step 7: Generate Document - Always visible */}
            {renderStep(
              7,
              t("generate_document") || "Generate Document",
              <BlockStack gap="200">
                {isApproved ? (
                  <>
                    {isFinanceFormSent ? (
                      <BlockStack gap="200">
                        {fileEntity?.id && (
                          <BlockStack gap="200">
                            {/* On touch screens the link is a 44px box; centred, the Badge keeps its own height. */}
                            <InlineStack gap="200" align="start" blockAlign="center">
                              <Link
                                url={`${API_URL}/${companyId}/files/download/${fileEntity.id}`}
                                target="_blank"
                              >
                                {fileEntity?.title ||
                                  fileEntity?.name ||
                                  t("download_file")}
                              </Link>
                              {isFileWithdrawn && (
                                <Badge tone="warning">{t("withdrawn_badge")}</Badge>
                              )}
                            </InlineStack>
                            {!fileEntity?.isSigned && !isFileWithdrawn && (
                              <InlineStack align="center">
                                <Button
                                  onClick={() => {
                                    dispatch(setModalxComponent("OrderDocumentForm"));
                                    dispatch(
                                      setModalxComponentProps({
                                        customerId: customer.id,
                                        template: "",
                                        fileId: fileEntity.relatedTo || "",
                                        assignedUserId: "",
                                        id: fileEntity.id,
                                        subject: "customer",
                                        year: financeDetail?.year,
                                        financeId: financeDetail?.financeRecord?.id,
                                        lockedTemplateId: fileEntity.templateId?.id || fileEntity.templateId || "",
                                      })
                                    );
                                  }}
                                >
                                  {t("edit")}
                                </Button>
                              </InlineStack>
                            )}
                            {canWithdrawFinanceDocument && (
                              <InlineStack align="center">
                                <Button
                                  tone="critical"
                                  onClick={handleWithdrawClick}
                                >
                                  {t("withdraw_document")}
                                </Button>
                              </InlineStack>
                            )}
                          </BlockStack>
                        )}
                      </BlockStack>
                    ) : (
                      <>
                        <Text as="p" tone="subdued">
                          {t("generate_document_description")}
                        </Text>
                        <Button
                          loading={isLoadingForApprove}
                          disabled={!isFormValid()}
                          onClick={financeSignHandler}
                          variant="primary"
                        >
                          {t("generate_form_and_send_for_customer")}
                        </Button>
                      </>
                    )}
                  </>
                ) : (
                  <Text as="p" tone="subdued">
                    {t("complete_previous_step")}
                  </Text>
                )}
              </BlockStack>
            )}

            {/* Step 8: Collect Signatures - Always visible */}
            {renderStep(
              8,
              t("collect_signatures"),
              <BlockStack gap="200">
                {isFinanceFormSent ? (
                  <>
                    {isFinanceActive ? (
                      <Text as="p" tone="success">
                        {t("finance_activated")}
                      </Text>
                    ) : (
                      <>
                        {/* Manual sign type actions */}
                        {!isFinanceActive &&
                          !isFileWithdrawn &&
                          isFinanceFormSent &&
                          fileEntity?.signType === "Manual" && (
                            <>
                              <SigningHint>
                                {t("manual_signing_awaiting_client")}
                              </SigningHint>
                              <InlineStack gap="200">
                                <ReplaceFile
                                  title={t("upload_signed_document")}
                                  onChange={handleOnFileChange}
                                  subject="customer"
                                  parentFileId={fileEntity.id}
                                  year={financeDetail?.year}
                                  financeId={financeDetail?.financeRecord?.id}
                                />
                                <Button
                                  onClick={() => {
                                    handleSendEmailWithAttachment(
                                      fileEntity.id,
                                      customer.id
                                    );
                                  }}
                                >
                                  {t("send_email")}
                                </Button>
                              </InlineStack>
                            </>
                          )}

                        {/* Dokobit sign type - Cancel request if fileToken exists.
                            Not offered on a template-generated document — the
                            backend rejects the removal anyway; withdraw the
                            whole document instead (see Step 7 above). */}
                        {!fileEntity?.isSigned &&
                          !isFileSignatureLocked &&
                          !isFinanceActive &&
                          isFinanceFormSent &&
                          fileEntity?.signType === "Dokobit" &&
                          fileEntity?.dokobit?.fileToken && (
                            <div style={{ marginTop: "5px" }}>
                              <Button
                                onClick={() => {
                                  handleCancelSignerRequest(
                                    fileEntity.id,
                                    "",
                                    "",
                                    customer.id,
                                    "",
                                    ""
                                  );
                                }}
                              >
                                {t("cancel_sign_request")}
                              </Button>
                            </div>
                          )}

                        {/* Dokobit sign type - Sign request when contactSigners exist and signingStepsRequired === 1 (only if fileToken doesn't exist and canMakeNewRequest is true) */}
                        {contactSigners.length > 0 &&
                          !fileEntity?.isSigned &&
                          !isFileWithdrawn &&
                          fileEntity?.signType === "Dokobit" &&
                          signingStepsRequired &&
                          signingStepsRequired === 1 &&
                          !fileEntity?.dokobit?.fileToken &&
                          fileEntity?.dokobit?.canMakeNewRequest !== false && (
                            <div style={{ marginTop: "5px" }}>
                              <Button
                                onClick={() => {
                                  handleUploadClick(
                                    fileEntity.id,
                                    contactSigners,
                                    permissionUserSigner,
                                    customer.id,
                                    userSigner,
                                    "Dokobit"
                                  );
                                }}
                              >
                                {t("sign_request")}
                              </Button>
                            </div>
                          )}

                        {/* Two-step manual signing: the client's copy is uploaded, our company still has to sign it in Dokobit. Shown whether or not the request was already sent. */}
                        {!fileEntity?.isSigned &&
                          !isFileWithdrawn &&
                          fileEntity?.signType === "Dokobit" &&
                          signingStepsRequired === 2 && (
                            <SigningHint>
                              {fileEntity?.dokobit?.fileToken
                                ? t("manual_signing_awaiting_company_sent")
                                : t("manual_signing_awaiting_company")}
                            </SigningHint>
                          )}

                        {/* Dokobit sign type - Sign request when signingStepsRequired === 2 (only if fileToken doesn't exist and canMakeNewRequest is true) */}
                        {!fileEntity?.isSigned &&
                          !isFileWithdrawn &&
                          fileEntity?.fileId &&
                          signingStepsRequired &&
                          signingStepsRequired === 2 &&
                          !fileEntity?.dokobit?.fileToken &&
                          fileEntity?.dokobit?.canMakeNewRequest !== false &&
                          fileEntity?.signType === "Dokobit" && (
                            <div style={{ marginTop: "5px" }}>
                              <Button
                                onClick={() => {
                                  FileSignUserAssignHandler(
                                    fileEntity.id,
                                    "",
                                    permissionUserSigner,
                                    userSigner
                                  );
                                }}
                              >
                                {t("sign_request_company_side")}
                              </Button>
                            </div>
                          )}
                      </>
                    )}
                  </>
                ) : (
                  <Text as="p" tone="subdued">
                    {t("complete_previous_step")}
                  </Text>
                )}
              </BlockStack>
            )}

            {/* Step 9: Create Order - Always visible */}
            {renderStep(
              9,
              t("create_order"),
              <BlockStack gap="200">
                {isFinanceActive ? (
                  <>
                    <Text as="p" tone="subdued">
                      {t("order_creation_unlocked")}
                    </Text>
                    <Button onClick={handleCreateOrder} variant="primary">
                      {t("create_order")}
                    </Button>
                  </>
                ) : (
                  <Text as="p" tone="subdued">
                    {t("complete_all_steps_to_unlock")}
                  </Text>
                )}
              </BlockStack>,
              false // No connector after last step
            )}
          </BlockStack>
        </div>
      </BlockStack>
    </Card>
  );
}

export default CustomerFinanceForm;


